// Fase 21C-4B: lógica pura del carrito del mini-POS de Ventas Otros. Aislada aquí (mismo criterio
// que otherSaleTax.ts / otherSaleProductForm.ts) para probarla sin DOM — el frontend no tiene
// jsdom/testing-library. El backend sigue siendo la fuente de verdad al confirmar; esta lógica es
// solo para el estado del carrito y la VISTA PREVIA de importes.
import type { OtherSaleProduct, OtherSaleTaxMode } from '../types/otherSales';
import type { OtherSaleCheckoutLineInput } from '../types/otherSales';
import { ApiError, getErrorMessage } from '../api/client';
import { calculateOtherSaleTaxBreakdown } from './otherSaleTax';

export interface CartLine {
  productId: string;
  name: string;
  /** Snapshot visible del precio unitario (string, ej. "25.00") — solo para mostrar/estimar. */
  unitPrice: string;
  taxMode: OtherSaleTaxMode;
  taxPercentage: string | null;
  quantity: number;
}

export interface CartPreview {
  subtotalAmount: number;
  taxAmount: number;
  totalAmount: number;
}

/** Crea una línea de carrito a partir de un producto del catálogo (cantidad inicial 1). */
export function cartLineFromProduct(product: Pick<OtherSaleProduct, 'id' | 'name' | 'price' | 'taxMode' | 'taxPercentage'>): CartLine {
  return {
    productId: product.id,
    name: product.name,
    unitPrice: product.price,
    taxMode: product.taxMode,
    taxPercentage: product.taxPercentage,
    quantity: 1,
  };
}

/** Agregar un producto: si ya está en el carrito incrementa su cantidad; nunca duplica líneas. */
export function addProductToCart(cart: CartLine[], product: Pick<OtherSaleProduct, 'id' | 'name' | 'price' | 'taxMode' | 'taxPercentage'>): CartLine[] {
  const existing = cart.find((line) => line.productId === product.id);
  if (existing) return changeQuantity(cart, product.id, 1);
  return [...cart, cartLineFromProduct(product)];
}

/**
 * Cambiar la cantidad de una línea por `delta` (ej. +1 / −1). Si la cantidad resultante es ≤ 0, la
 * línea se elimina (nunca queda en 0 ni negativa). La cantidad siempre se mantiene entera.
 */
export function changeQuantity(cart: CartLine[], productId: string, delta: number): CartLine[] {
  return cart
    .map((line) => (line.productId === productId ? { ...line, quantity: Math.trunc(line.quantity + delta) } : line))
    .filter((line) => line.quantity > 0);
}

/** Fijar la cantidad exacta de una línea (entera). Si es ≤ 0, elimina la línea. */
export function setLineQuantity(cart: CartLine[], productId: string, quantity: number): CartLine[] {
  const normalized = Math.trunc(quantity);
  return cart
    .map((line) => (line.productId === productId ? { ...line, quantity: normalized } : line))
    .filter((line) => line.quantity > 0);
}

/** Eliminar una línea del carrito explícitamente. */
export function removeFromCart(cart: CartLine[], productId: string): CartLine[] {
  return cart.filter((line) => line.productId !== productId);
}

export function cartQuantityFor(cart: CartLine[], productId: string): number {
  return cart.find((line) => line.productId === productId)?.quantity ?? 0;
}

export function cartItemCount(cart: CartLine[]): number {
  return cart.reduce((sum, line) => sum + line.quantity, 0);
}

export function isCartEmpty(cart: CartLine[]): boolean {
  return cart.length === 0;
}

/**
 * Payload de líneas para POST /api/other-sales/checkout: EXCLUSIVAMENTE `productId` y `quantity`.
 * Nunca precios, impuestos ni totales — el servidor los calcula desde el catálogo. Se descartan
 * líneas con cantidad ≤ 0 por seguridad (no deberían existir: se eliminan al llegar a 0).
 */
export function buildCheckoutLines(cart: CartLine[]): OtherSaleCheckoutLineInput[] {
  return cart
    .filter((line) => line.quantity > 0)
    .map((line) => ({ productId: line.productId, quantity: line.quantity }));
}

/**
 * Vista previa de importes (estimada) — replica la semántica fiscal del backend POR LÍNEA
 * (cantidad × precio, luego NONE/INCLUDED/CUSTOM con redondeo a 2 decimales) y suma las líneas ya
 * redondeadas, igual que el servidor. El total DEFINITIVO siempre es el que devuelve el checkout.
 */
export function calculateCartPreview(cart: CartLine[]): CartPreview {
  let subtotalAmount = 0;
  let taxAmount = 0;
  let totalAmount = 0;
  for (const line of cart) {
    const lineAmount = Number(line.unitPrice) * line.quantity;
    const percentage = line.taxPercentage === null ? null : Number(line.taxPercentage);
    const breakdown = calculateOtherSaleTaxBreakdown(lineAmount, line.taxMode, percentage);
    subtotalAmount = round2(subtotalAmount + breakdown.subtotalAmount);
    taxAmount = round2(taxAmount + breakdown.taxAmount);
    totalAmount = round2(totalAmount + breakdown.totalAmount);
  }
  return { subtotalAmount, taxAmount, totalAmount };
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Fase 21C-5: etiqueta corta del tratamiento fiscal para tarjetas/carrito. "Sin IVA" / "IVA
 * incluido" / "IVA 16%" (sin decimales cuando el porcentaje es entero, para no saturar).
 */
export function formatProductTaxLabel(product: { taxMode: OtherSaleTaxMode; taxPercentage: string | null }): string {
  if (product.taxMode === 'NONE') return 'Sin IVA';
  if (product.taxMode === 'INCLUDED') return 'IVA incluido';
  if (product.taxPercentage === null || product.taxPercentage === '') return 'IVA personalizado';
  const pct = Number(product.taxPercentage);
  const text = Number.isInteger(pct) ? String(pct) : pct.toFixed(1).replace(/\.0$/, '');
  return `IVA ${text}%`;
}

export interface CheckoutErrorInfo {
  /** true = no se sabe si la venta se creó (red/timeout/5xx); false = rechazo definitivo del servidor. */
  ambiguous: boolean;
  message: string;
}

/**
 * Fase 21C-4C: clasifica un error del checkout para no mentirle al usuario sobre el resultado.
 * - RECHAZO DEFINITIVO (4xx: 400/401/403/404/409/422…): el servidor respondió y rechazó ANTES de
 *   crear la venta → no se creó nada. Se muestra el mensaje del backend y se conserva el carrito.
 * - AMBIGUO (sin respuesta del servidor: red/timeout = status 0, o 5xx): no se sabe si la venta se
 *   creó → conservar carrito, pedir revisar el Historial antes de reconfirmar, sin retry automático.
 *   La solución definitiva (idempotency key) requiere soporte backend en una fase futura.
 */
/**
 * PAYPAL-CURRENCY-01: ¿el servidor se negó a entregar el intento porque la sesión del lector de
 * este equipo es de otra cuenta? Devuelve el mensaje del SERVIDOR, que es quien sabe qué esperaba.
 * Sin intento no se abrió el lector, así que no hay dinero en juego y el aviso puede decirlo.
 */
export function deviceSessionBlockMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const code = String((error.payload as { error?: unknown } | undefined)?.error ?? '');
  if (code !== 'DEVICE_SESSION_CURRENCY_MISMATCH' && code !== 'DEVICE_SESSION_ACCOUNT_MISMATCH') return undefined;
  return getErrorMessage(error, 'El lector de este equipo está en otra cuenta de Zettle. No se abrió el lector y no se registró ningún cobro.');
}

export function classifyCheckoutError(error: unknown): CheckoutErrorInfo {
  const status = error instanceof ApiError ? error.status : undefined;
  const ambiguous = status === undefined || status === 0 || status >= 500;
  if (ambiguous) {
    return {
      ambiguous: true,
      message: 'No pudimos confirmar el resultado de la venta (posible problema de conexión). Revisa el Historial antes de volver a confirmar: es posible que la venta ya se haya registrado.',
    };
  }
  return { ambiguous: false, message: getErrorMessage(error, 'No fue posible registrar la venta.') };
}

/**
 * FI-53: por qué no se pudo completar una venta que YA está cobrada. Un rechazo del servidor se dice
 * con su motivo (existencias, un producto desactivado…). Sin respuesta, o con un 5xx, no se sabe si
 * la venta quedó completada: no se afirma ninguna de las dos cosas, se recuerda lo único seguro —el
 * cobro está confirmado— y se ofrece reintentar, que es idempotente y no cobra.
 */
export function describePaidCompletionError(error: unknown): CheckoutErrorInfo {
  const info = classifyCheckoutError(error);
  if (!info.ambiguous) return info;
  return {
    ambiguous: true,
    message: 'No pudimos confirmar si la venta quedó completada (posible problema de conexión). El cobro ya está confirmado: no vuelvas a cobrar. Toca «Reintentar de forma segura».',
  };
}

/** Los dos códigos con los que el servidor niega cobrar, cancelar o abrir otro intento porque la venta YA tiene un cobro confirmado. */
const PAYMENT_CONFIRMED_CODES: ReadonlySet<string> = new Set(['SALE_HAS_SUCCEEDED_PAYMENT', 'PAYMENT_ALREADY_SUCCEEDED']);

/** FI-53: ¿el servidor rechazó la operación diciendo que la venta ya tiene un cobro confirmado? */
export function saysPaymentConfirmed(error: unknown): boolean {
  return error instanceof ApiError && PAYMENT_CONFIRMED_CODES.has(String((error.payload as { error?: unknown } | undefined)?.error ?? ''));
}

/** ¿El servidor RESPONDIÓ (aunque fuera con un error)? Sin respuesta —red caída— no hay a quién preguntarle nada. */
export function serverAnswered(error: unknown): boolean {
  return error instanceof ApiError && error.status !== 0;
}

// ── Pre-client UX hardening (P0): resolución de la tienda efectiva del POS ──────────────────────
// `stores` llega ASINCRÓNICAMENTE a OtherSalePos (la página lo carga con getStores()). Si el primer
// render ocurre con stores=[], un initializer de useState congelaba storeId='' para siempre y el POS
// mostraba un "catálogo vacío" FALSO teniendo productos activos (hallazgo P0 de la auditoría visual).
// Esta función decide la tienda efectiva cada vez que `stores` llega o cambia, sin pisar jamás una
// selección legítima del usuario.
export function resolveEffectiveStoreId(
  currentStoreId: string,
  stores: Array<{ id: string }>,
  defaultStoreId: string | undefined,
): string {
  // Sin tiendas todavía (carga en curso o usuario sin tiendas): no hay nada que decidir.
  if (stores.length === 0) return currentStoreId;
  // Selección vigente y válida: se respeta SIEMPRE (nunca se resetea por un re-render).
  if (currentStoreId && stores.some((store) => store.id === currentStoreId)) return currentStoreId;
  // Sin selección o la tienda ya no existe: default del scope del rol, o la primera accesible.
  return defaultStoreId ?? stores[0]?.id ?? '';
}

// Deuda (k): el carrito solo se vacía si la venta cancelada es la que SALIÓ de este carrito. Cancelar
// una venta recuperada con «Continuar» no puede borrar lo que la persona ya tenía escaneado para otro
// cliente; y un carrito que sobrevive a su propia cancelación se le suma al cliente siguiente.
export function shouldClearCartOnCancel(canceledSaleId: string, cartSaleId: string | undefined): boolean {
  return cartSaleId !== undefined && canceledSaleId === cartSaleId;
}

export type PosStoreState = 'loading' | 'error' | 'unassigned' | 'ready';

// Deuda (m): `stores = []` NO significa «sin tienda asignada» mientras la carga sigue en curso; la
// pantalla lo afirmaba durante un instante al abrir Ventas Otros. Misma regla que posCatalogState: el
// vacío solo se afirma con la carga TERMINADA Y BIEN. Una carga que falló tampoco es «no tienes
// tiendas»: es «no pudimos preguntarlo», que se dice distinto y ofrece reintentar.
export function posStoreState(
  storeId: string,
  stores: Array<{ id: string }>,
  storesLoaded: boolean,
  storesFailed = false,
): PosStoreState {
  if (storeId || stores.length > 0) return 'ready';
  if (storesFailed) return 'error';
  return storesLoaded ? 'unassigned' : 'loading';
}

export type PosCatalogState = 'loading' | 'error' | 'empty' | 'ready';

// Estado visible del catálogo del POS. Regla P0: "vacío" solo se afirma cuando la tienda está
// RESUELTA y el catálogo de ESA tienda ya respondió — nunca mientras la tienda o la carga siguen
// pendientes (eso es 'loading'), ni cuando la carga falló (eso es 'error', con reintento).
export function posCatalogState(
  storeId: string,
  products: unknown[] | undefined,
  error: string | undefined,
): PosCatalogState {
  if (error) return 'error';
  if (!storeId || products === undefined) return 'loading';
  return products.length === 0 ? 'empty' : 'ready';
}
